import React, { useEffect, useState } from "react";
import api, { ESTADO_PETICION } from "@/lib/api";
import { Link } from "react-router-dom";
import { FileStack, Building2, Package, Users, Euro, TrendingUp, Factory, Target, AlertTriangle, Calendar as CalendarIcon, CheckSquare, Receipt, Truck, Wallet, Clock as ClockIcon, FileCheck2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function AgingBucket({ color, label, amount, count, testid, plain = false }) {
  const cls = {
    red: "bg-red-500/10 border-red-500/40 text-red-300",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    zinc: "bg-zinc-800/40 border-zinc-700/40 text-zinc-200",
    "zinc-dim": "bg-zinc-800/20 border-zinc-800/40 text-zinc-400",
  }[color] || "bg-zinc-800/40 border-zinc-700/40 text-zinc-200";
  return (
    <div className={`border rounded-lg p-2.5 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-bold mt-0.5 font-mono">
        {plain
          ? amount
          : amount.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"}
      </div>
      {!plain && (
        <div className="text-[10px] opacity-70">{count} factura{count !== 1 ? "s" : ""}</div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent = "amber" }) {  const colors = {
    amber: "from-amber-500/20 to-amber-700/0 text-amber-300 border-amber-500/30",
    blue: "from-blue-500/20 to-blue-700/0 text-blue-300 border-blue-500/30",
    emerald: "from-emerald-500/20 to-emerald-700/0 text-emerald-300 border-emerald-500/30",
    violet: "from-violet-500/20 to-violet-700/0 text-violet-300 border-violet-500/30",
  }[accent];
  return (
    <div className={`relative rounded-xl border bg-gradient-to-br ${colors} p-5 overflow-hidden`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">{label}</div>
        <Icon className="w-4 h-4 opacity-80" />
      </div>
      <div className="mt-3 text-3xl font-bold text-zinc-50">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-400">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { hasRole } = useAuth();
  const puedeVerLeads = hasRole("admin") || hasRole("comercial");
  const puedeVerFacturas = hasRole("admin") || hasRole("gestoria");
  const [data, setData] = useState({ peticiones: [], clientes: [], materiales: [], users: [] });
  const [leadsPend, setLeadsPend] = useState({ vencidos: [], hoy: [], count: 0 });
  const [tareasPend, setTareasPend] = useState({ vencidas: [], hoy: [], urgentes: [], count: 0 });
  const [facturasResumen, setFacturasResumen] = useState(null);
  const [vehiculosAvisos, setVehiculosAvisos] = useState(null);
  const [tesoreria, setTesoreria] = useState(null);
  const [facturacionMes, setFacturacionMes] = useState(null); // D1 · Iter 98
  const [reconciliando, setReconciliando] = useState(false);
  const [reportesPend, setReportesPend] = useState(null); // {total, por_validar, por_enviar}
  useEffect(() => {
    // Widget Reportes pendientes (admin / oficina)
    if (!hasRole("admin") && !hasRole("produccion") && !hasRole("logistica") && !hasRole("comercial")) return;
    api.get("/reportes-pendientes")
      .then((r) => {
        const arr = r.data || [];
        const por_validar = arr.filter((x) => (x.parada?.validacion?.estado || "pendiente") === "pendiente").length;
        const por_enviar = arr.filter((x) => x.parada?.validacion?.estado === "aprobado").length;
        setReportesPend({ total: arr.length, por_validar, por_enviar });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    Promise.all([
      api.get("/peticiones"), api.get("/clientes"),
      api.get("/materiales"), api.get("/users"),
    ]).then(([p, c, m, u]) => setData({
      peticiones: p.data, clientes: c.data, materiales: m.data, users: u.data
    })).catch(() => {});
  }, []);
  useEffect(() => {
    if (!puedeVerLeads) return;
    api.get("/leads/pendientes")
      .then(({ data }) => setLeadsPend(data))
      .catch(() => {});
  }, [puedeVerLeads]);

  useEffect(() => {
    if (!puedeVerFacturas) return;
    api.get("/facturas-externos/resumen", { params: { year: new Date().getFullYear() } })
      .then(({ data }) => setFacturasResumen(data))
      .catch(() => {});
  }, [puedeVerFacturas]);

  useEffect(() => {
    if (!hasRole("admin") && !hasRole("logistica")) return;
    api.get("/vehiculos/avisos")
      .then(({ data }) => setVehiculosAvisos(data))
      .catch(() => {});
  }, [hasRole]);

  // Widget Tesorería FD (admin/administracion/comercial)
  useEffect(() => {
    if (!hasRole("admin") && !hasRole("administracion") && !hasRole("comercial")) return;
    api.get("/tesoreria/dashboard", { params: { periodo: "mes_actual" } })
      .then(({ data }) => setTesoreria(data))
      .catch(() => {});
    api.get("/dashboard/peticiones-facturacion-mes")
      .then(({ data }) => setFacturacionMes(data))
      .catch(() => {});
  }, [hasRole]);

  const handleReconciliar = async () => {
    if (reconciliando) return;
    setReconciliando(true);
    try {
      const { data } = await api.post("/peticiones/reconciliar-facturas-fd");
      const msg = data.ok
        ? `✅ ${data.n_peticiones_enlazadas} peticiones enlazadas con facturas FD (${data.n_facturas_procesadas} facturas procesadas).`
        : `❌ ${data.error || "Error"}`;
      alert(msg);
      // Recargar KPIs
      const { data: fm } = await api.get("/dashboard/peticiones-facturacion-mes");
      setFacturacionMes(fm);
    } catch (e) {
      alert("Error reconciliando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setReconciliando(false);
    }
  };

  const totalPresupuesto = data.peticiones.reduce((s, p) => s + (p.presupuesto_total || 0), 0);
  const totalCoste = data.peticiones.reduce((s, p) => s + (p.coste_estimado || 0), 0);
  const margen = totalPresupuesto > 0 ? (((totalPresupuesto - totalCoste) / totalPresupuesto) * 100).toFixed(1) : 0;
  const enProd = data.peticiones.filter((p) => p.estado === "en_produccion").length;

  return (
    <div className="p-8 max-w-[1800px]">
      <div className="flex items-center gap-3 mb-8">
        <Factory className="w-7 h-7 text-amber-400" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Centro de control</h1>
          <p className="text-sm text-zinc-400">Resumen de operaciones — RotulaciónDNF</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={FileStack} label="Peticiones activas" value={data.peticiones.length}
          sub={`${enProd} en producción`} accent="amber" />
        <Stat icon={Euro} label="Presupuesto cartera" value={`${totalPresupuesto.toLocaleString("es-ES")} €`}
          sub={`Coste estimado: ${totalCoste.toLocaleString("es-ES")} €`} accent="blue" />
        <Stat icon={TrendingUp} label="Margen medio" value={`${margen}%`}
          sub="Sobre presupuesto total" accent="emerald" />
        <Stat icon={Building2} label="Clientes / Equipo" value={`${data.clientes.length} / ${data.users.length}`}
          sub={`${data.materiales.length} materiales en catálogo`} accent="violet" />
      </div>

      {/* 🔔 Widget Reportes pendientes — visible para admin/oficina */}
      {reportesPend && reportesPend.total > 0 && (
        <Link
          to="/reportes"
          data-testid="dashboard-reportes-pendientes"
          className="block mb-8 bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent border-l-4 border-amber-500 hover:border-amber-400 hover:from-amber-500/20 rounded-r-xl p-4 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="shrink-0 w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center relative">
              <FileCheck2 className="w-6 h-6 text-amber-300" />
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-zinc-950 text-[11px] font-bold flex items-center justify-center"
                    data-testid="reportes-pend-badge">
                {reportesPend.total}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-200">
                {reportesPend.total} reporte{reportesPend.total !== 1 ? "s" : ""} de montaje esperando tu acción
              </div>
              <div className="text-xs text-zinc-400 mt-0.5 flex items-center gap-3 flex-wrap">
                {reportesPend.por_validar > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                    {reportesPend.por_validar} por validar
                  </span>
                )}
                {reportesPend.por_enviar > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                    {reportesPend.por_enviar} validado{reportesPend.por_enviar !== 1 ? "s" : ""} · pendiente de enviar/albaranar
                  </span>
                )}
              </div>
            </div>
            <div className="text-xs text-amber-300 font-semibold shrink-0">Revisar →</div>
          </div>
        </Link>
      )}

      {/* Facturación + Peticiones del mes (admin) · Iter 98 unificado FD */}
      {hasRole("admin") && facturacionMes && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4" data-testid="dashboard-fact-mes-kpi">
          <Link to="/peticiones?solo_montadas_mes=1"
            className="block bg-gradient-to-br from-emerald-500/15 to-emerald-700/0 border border-emerald-500/30 rounded-xl p-4 hover:border-emerald-500/60 transition"
            data-testid="kpi-mes-montadas">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80">Montadas este mes</div>
              <CheckSquare className="w-3.5 h-3.5 text-emerald-300/70" />
            </div>
            <div className="text-2xl font-bold text-emerald-200">{facturacionMes.peticiones_montadas}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">peticiones cerradas</div>
          </Link>
          <Link to="/tesoreria"
            className="block bg-gradient-to-br from-sky-500/15 to-sky-700/0 border border-sky-500/30 rounded-xl p-4 hover:border-sky-500/60 transition"
            data-testid="kpi-mes-facturadas">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-sky-300/80">Facturadas este mes</div>
              <Receipt className="w-3.5 h-3.5 text-sky-300/70" />
            </div>
            <div className="text-2xl font-bold text-sky-200">{facturacionMes.peticiones_facturadas}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{facturacionMes.importe_facturado_real.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€ facturados</div>
          </Link>
          <Link to="/peticiones?facturacion=pendiente"
            className={`block bg-gradient-to-br ${facturacionMes.peticiones_pendientes_facturar > 0 ? "from-amber-500/15 to-amber-700/0 border-amber-500/30 hover:border-amber-500/60" : "from-zinc-700/15 to-zinc-800/0 border-zinc-700/30"} border rounded-xl p-4 transition`}
            data-testid="kpi-mes-pendientes">
            <div className="flex items-center justify-between mb-1">
              <div className={`text-[10px] uppercase tracking-[0.18em] ${facturacionMes.peticiones_pendientes_facturar > 0 ? "text-amber-300/80" : "text-zinc-500/80"}`}>Pendientes facturar</div>
              <AlertTriangle className={`w-3.5 h-3.5 ${facturacionMes.peticiones_pendientes_facturar > 0 ? "text-amber-300/70" : "text-zinc-600/70"}`} />
            </div>
            <div className={`text-2xl font-bold ${facturacionMes.peticiones_pendientes_facturar > 0 ? "text-amber-200" : "text-zinc-400"}`}>{facturacionMes.peticiones_pendientes_facturar}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {facturacionMes.peticiones_pendientes_facturar > 0 ? "montadas sin factura" : "✅ todo facturado"}
            </div>
          </Link>
          <button
            onClick={handleReconciliar}
            disabled={reconciliando}
            data-testid="btn-reconciliar-fd"
            className="bg-gradient-to-br from-violet-500/15 to-violet-700/0 border border-violet-500/30 rounded-xl p-4 hover:border-violet-500/60 transition text-left disabled:opacity-60">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-violet-300/80">Reconciliar FD</div>
              <FileCheck2 className="w-3.5 h-3.5 text-violet-300/70" />
            </div>
            <div className="text-base font-semibold text-violet-200">
              {reconciliando ? "Procesando..." : "🔄 Sincronizar enlaces"}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight">
              Detecta códigos DNF en facturas FD<br/>y enlaza con peticiones
            </div>
          </button>
        </div>
      )}

      {/* Tesorería FacturaDirecta (admin/administracion/comercial) */}
      {tesoreria && tesoreria.kpis && (
        <>
          {/* Iter 88 — Aviso en Dashboard si la caché FD está vacía (ej: scheduler nunca ha corrido) */}
          {tesoreria.kpis.count_facturas === 0 && tesoreria.kpis.total_facturado === 0 && (
            <Link to="/tesoreria"
                  data-testid="dashboard-tesoreria-empty-hint"
                  className="mb-3 flex items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-2.5 hover:border-amber-500/60 transition">
              <div className="flex items-center gap-2 text-xs text-amber-200">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                Los KPIs de FacturaDirecta están vacíos. Es posible que la caché aún no se haya sincronizado.
              </div>
              <span className="text-xs text-amber-300 underline">Ir a Tesorería para sincronizar →</span>
            </Link>
          )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="dashboard-tesoreria-widget">
          <Link to="/tesoreria" className="block bg-gradient-to-br from-sky-500/15 to-sky-700/0 border border-sky-500/30 rounded-xl p-5 hover:border-sky-500/60 transition"
                data-testid="card-fd-facturado-mes">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-sky-300/80">Facturado este mes</div>
              <Wallet className="w-4 h-4 text-sky-300/70" />
            </div>
            <div className="text-3xl font-bold text-sky-200 mt-2">
              {tesoreria.kpis.total_facturado.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {tesoreria.kpis.count_facturas} factura{tesoreria.kpis.count_facturas !== 1 ? "s" : ""} · cobrado {tesoreria.kpis.total_cobrado.toLocaleString("es-ES", { minimumFractionDigits: 0 })}€
            </div>
          </Link>

          <Link to="/tesoreria" className="block bg-gradient-to-br from-amber-500/15 to-amber-700/0 border border-amber-500/30 rounded-xl p-5 hover:border-amber-500/60 transition"
                data-testid="card-fd-pendiente">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/80">Pendiente cobro</div>
              <ClockIcon className="w-4 h-4 text-amber-300/70" />
            </div>
            <div className="text-3xl font-bold text-amber-200 mt-2">
              {tesoreria.kpis.total_pendiente.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              del mes actual
            </div>
          </Link>

          <Link to="/tesoreria" className={`block bg-gradient-to-br ${tesoreria.kpis.total_vencido > 0 ? "from-rose-500/15 to-rose-700/0 border-rose-500/30 hover:border-rose-500/60" : "from-zinc-700/15 to-zinc-800/0 border-zinc-700/30"} border rounded-xl p-5 transition`}
                data-testid="card-fd-vencido">
            <div className="flex items-center justify-between mb-1">
              <div className={`text-[11px] uppercase tracking-[0.18em] ${tesoreria.kpis.total_vencido > 0 ? "text-rose-300/80" : "text-zinc-500/80"}`}>Vencido</div>
              <AlertTriangle className={`w-4 h-4 ${tesoreria.kpis.total_vencido > 0 ? "text-rose-300/70" : "text-zinc-600/70"}`} />
            </div>
            <div className={`text-3xl font-bold mt-2 ${tesoreria.kpis.total_vencido > 0 ? "text-rose-200" : "text-zinc-400"}`}>
              {tesoreria.kpis.total_vencido.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {tesoreria.kpis.count_vencido > 0
                ? <><b className="text-rose-300">{tesoreria.kpis.count_vencido}</b> factura{tesoreria.kpis.count_vencido !== 1 ? "s" : ""} fuera de plazo</>
                : "✅ Sin vencimientos"}
            </div>
          </Link>
        </div>
        </>
      )}

      {puedeVerLeads && leadsPend.count > 0 && (
        <div className="mb-8 bg-zinc-900/60 border border-zinc-800 rounded-xl p-5"
          data-testid="dashboard-tareas-pendientes">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-red-400" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-200">
                Tareas pendientes en CRM
              </h2>
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                {leadsPend.count}
              </span>
            </div>
            <Link to="/leads" className="text-xs text-amber-300 hover:text-amber-200">
              Ir a Leads →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {leadsPend.vencidos.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs text-red-400 font-semibold mb-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Vencidas ({leadsPend.vencidos.length})
                </div>
                <div className="space-y-1.5">
                  {leadsPend.vencidos.slice(0, 5).map((l) => (
                    <Link key={l.id} to="/leads"
                      data-testid={`tarea-vencida-${l.id}`}
                      className="block bg-red-500/10 border border-red-500/30 rounded p-2 text-xs hover:bg-red-500/20">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-zinc-100 truncate">{l.empresa}</div>
                        <div className="text-red-300 shrink-0">{l.proxima_accion_fecha}</div>
                      </div>
                      <div className="text-zinc-400 truncate">{l.proxima_accion}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {leadsPend.hoy.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs text-amber-400 font-semibold mb-2">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  Hoy ({leadsPend.hoy.length})
                </div>
                <div className="space-y-1.5">
                  {leadsPend.hoy.slice(0, 5).map((l) => (
                    <Link key={l.id} to="/leads"
                      data-testid={`tarea-hoy-${l.id}`}
                      className="block bg-amber-500/10 border border-amber-500/30 rounded p-2 text-xs hover:bg-amber-500/20">
                      <div className="font-medium text-zinc-100 truncate">{l.empresa}</div>
                      <div className="text-zinc-400 truncate">{l.proxima_accion}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {puedeVerFacturas && facturasResumen && facturasResumen.totales.facturas_pendientes > 0 && (
        <div className="mb-8 bg-zinc-900/60 border border-amber-500/30 rounded-xl p-5"
          data-testid="dashboard-facturas-pendientes">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-400" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-200">
                Facturas externas pendientes de pago
              </h2>
              <span className="bg-amber-500 text-zinc-950 text-[10px] font-bold rounded-full px-2 py-0.5"
                data-testid="facturas-pend-count">
                {facturasResumen.totales.facturas_pendientes}
              </span>
            </div>
            <Link to="/facturas-externos" className="text-xs text-amber-300 hover:text-amber-200"
              data-testid="facturas-pend-link">
              Ir a Facturas externos →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Pendiente total</div>
              <div className="text-2xl font-bold text-amber-200 mt-1" data-testid="facturas-pend-total">
                {facturasResumen.totales.pendiente.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">Pagado YTD</div>
              <div className="text-2xl font-bold text-emerald-200 mt-1">
                {facturasResumen.totales.pagado.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
            </div>
            <div className="bg-zinc-800/40 border border-zinc-700/40 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Facturado YTD</div>
              <div className="text-2xl font-bold text-zinc-100 mt-1">
                {facturasResumen.totales.facturado.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
            </div>
            <div className="bg-zinc-800/40 border border-zinc-700/40 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Nº facturas</div>
              <div className="text-2xl font-bold text-zinc-100 mt-1">
                {facturasResumen.totales.facturas}
              </div>
            </div>
          </div>

          {/* Aging buckets */}
          {facturasResumen.aging && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="facturas-aging">
              <AgingBucket color="red" label="Vencidas"
                amount={facturasResumen.aging.vencidas} count={facturasResumen.aging.vencidas_n}
                testid="aging-vencidas" />
              <AgingBucket color="amber" label={`Vencen ≤${facturasResumen.dias_aviso || 7}d`}
                amount={facturasResumen.aging.proximas_7d} count={facturasResumen.aging.proximas_7d_n}
                testid="aging-proximas" />
              <AgingBucket color="zinc" label="Futuras"
                amount={facturasResumen.aging.futuras} count={facturasResumen.aging.futuras_n}
                testid="aging-futuras" />
              <AgingBucket color="zinc-dim" label="Sin vencimiento"
                amount={facturasResumen.aging.sin_vencimiento} count={facturasResumen.aging.sin_vencimiento_n}
                testid="aging-sin-vto" />
            </div>
          )}

          {/* Próximas a vencer (vencidas + ≤dias_aviso) */}
          {facturasResumen.proximas_vencer && facturasResumen.proximas_vencer.length > 0 && (
            <div className="mb-4" data-testid="facturas-proximas-vencer">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Próximas a vencer / vencidas
              </div>
              <div className="space-y-1.5">
                {facturasResumen.proximas_vencer.slice(0, 5).map((p) => {
                  const dias = p.dias_para_vencer;
                  const vencida = dias < 0;
                  const cls = vencida
                    ? "bg-red-500/10 border-red-500/40 hover:bg-red-500/20"
                    : "bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20";
                  const txt = vencida
                    ? `Vencida hace ${Math.abs(dias)}d`
                    : (dias === 0 ? "Vence hoy" : `Vence en ${dias}d`);
                  return (
                    <Link key={p.id} to="/facturas-externos"
                      data-testid={`factura-prox-${p.id}`}
                      className={`flex items-center justify-between border rounded p-2 transition-colors ${cls}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-100 truncate">{p.emisor_nombre || "—"}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">
                          {p.numero || "Sin nº"} · Emisión {p.fecha_emision} · Vto {p.fecha_vencimiento}
                        </div>
                      </div>
                      <div className="flex flex-col items-end shrink-0 gap-0.5">
                        <div className={`text-sm font-mono font-semibold ${vencida ? "text-red-300" : "text-amber-300"}`}>
                          {p.total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </div>
                        <div className={`text-[10px] uppercase tracking-wider ${vencida ? "text-red-400" : "text-amber-400"}`}>{txt}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {facturasResumen.emisores.filter((e) => e.pendiente > 0).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                Mayores deudas por colaborador
              </div>
              <div className="space-y-1.5">
                {facturasResumen.emisores
                  .filter((e) => e.pendiente > 0)
                  .sort((a, b) => b.pendiente - a.pendiente)
                  .slice(0, 5)
                  .map((e) => (
                    <Link key={e.emisor_key} to="/facturas-externos"
                      data-testid={`facturas-pend-emisor-${e.emisor_key}`}
                      className="flex items-center justify-between bg-zinc-900/40 hover:bg-zinc-900/80 border border-zinc-800/60 rounded p-2 transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-100 truncate">{e.emisor_nombre || "Sin nombre"}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">{e.emisor_nif || "—"} · {e.facturas} factura{e.facturas !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="text-amber-300 font-mono font-semibold shrink-0">
                        {e.pendiente.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {vehiculosAvisos && (vehiculosAvisos.totales.vencidos + vehiculosAvisos.totales.proximos_30d) > 0 && (
        <div className="mb-8 bg-zinc-900/60 border border-red-500/30 rounded-xl p-5"
          data-testid="dashboard-vehiculos-avisos">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-red-400" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-200">
                Vehículos — ITV / seguro / revisión
              </h2>
            </div>
            <Link to="/vehiculos" className="text-xs text-red-300 hover:text-red-200"
              data-testid="vehiculos-avisos-link">
              Ir a Flota →
            </Link>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <AgingBucket color="red" label="Vencidos"
              amount={vehiculosAvisos.totales.vencidos} count={vehiculosAvisos.totales.vencidos}
              testid="veh-vencidos" plain />
            <AgingBucket color="amber" label="≤30 días"
              amount={vehiculosAvisos.totales.proximos_30d} count={vehiculosAvisos.totales.proximos_30d}
              testid="veh-prox30" plain />
            <AgingBucket color="zinc" label="≤60 días"
              amount={vehiculosAvisos.totales.proximos_60d} count={vehiculosAvisos.totales.proximos_60d}
              testid="veh-prox60" plain />
          </div>

          <div className="space-y-1.5">
            {[...vehiculosAvisos.vencidos, ...vehiculosAvisos.proximos_30d].slice(0, 6).map((v, i) => {
              const vencido = v.dias < 0;
              const cls = vencido
                ? "bg-red-500/10 border-red-500/40 hover:bg-red-500/20"
                : "bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20";
              const txt = vencido
                ? `Vencida hace ${Math.abs(v.dias)}d`
                : (v.dias === 0 ? "Vence hoy" : `Vence en ${v.dias}d`);
              return (
                <Link key={`${v.vehiculo_id}-${i}`} to="/vehiculos"
                  className={`flex items-center justify-between border rounded p-2 transition-colors ${cls}`}
                  data-testid={`veh-aviso-${v.vehiculo_id}-${v.tipo_aviso}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {v.nombre} <span className="text-xs text-zinc-500 font-mono">· {v.matricula || "—"}</span>
                    </div>
                    <div className="text-[11px] text-zinc-400">
                      <b>{v.tipo_aviso}</b> · {v.fecha}
                    </div>
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider ${vencido ? "text-red-400" : "text-amber-400"}`}>{txt}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">Últimas peticiones</h2>
            <Link to="/peticiones" className="text-xs text-amber-300 hover:text-amber-200">Ver todas →</Link>
          </div>
          <div className="space-y-2">
            {data.peticiones.slice(0, 8).map((p) => {
              const cli = data.clientes.find((c) => c.id === p.cliente_id);
              const est = ESTADO_PETICION[p.estado] || ESTADO_PETICION.borrador;
              return (
                <Link to={`/peticiones/${p.id}`} key={p.id}
                  data-testid={`dashboard-peticion-${p.codigo || p.id}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 hover:border-amber-500/40 hover:bg-zinc-900 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.nombre}</div>
                    <div className="text-xs text-zinc-500">{cli?.nombre || "—"} · {p.codigo || "Sin código"}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-xs text-zinc-400">{(p.presupuesto_total || 0).toLocaleString("es-ES")} €</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${est.color}`}>
                      {est.label}
                    </span>
                  </div>
                </Link>
              );
            })}
            {data.peticiones.length === 0 && (
              <div className="text-sm text-zinc-500 text-center py-8">Sin peticiones todavía</div>
            )}
          </div>
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300 mb-4">Equipo</h2>
          <div className="space-y-2">
            {data.users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-zinc-900/80">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-zinc-900 text-xs font-bold">
                  {(u.name || "?").slice(0, 1)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{(u.roles || []).join(", ")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
